import { useState } from 'react';
import type {
  ReactNode,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  SelectHTMLAttributes,
} from 'react';
import { NavIcon } from './NavIcon.tsx';

export function Field({
  label,
  hint,
  error,
  children,
  required,
  asGroup = false,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  required?: boolean;
  /**
   * Render the wrapper as a <div role="group"> instead of a <label>. Use this
   * when the control is a custom widget (e.g. a button-triggered dropdown): a
   * <label> forwards clicks on its inert areas (label text, hint, summary) to
   * the first labelable descendant — the trigger <button> — which would reopen
   * a panel the user just clicked away to close.
   */
  asGroup?: boolean;
}) {
  const body = (
    <>
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </>
  );
  if (asGroup) {
    return (
      <div role="group" aria-label={label} className="block">
        {body}
      </div>
    );
  }
  return <label className="block">{body}</label>;
}

const inputCls =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-slate-400';

export function TextInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${inputCls} ${className}`} {...props} />;
}

// A password field with a show/hide toggle. Accepts the same props as TextInput
// (the `type` is managed internally). `block`-display <span> keeps the markup
// valid inside Field's implicit <label>; the toggle is a real <button> so it's
// keyboard-operable and, being interactive content, won't forward its click to
// the labelled input.
export function PasswordInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative block">
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        className={`${inputCls} pr-10 ${className}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 transition-colors hover:text-slate-600"
      >
        <NavIcon name={visible ? 'eye-off' : 'eye'} size={18} />
      </button>
    </span>
  );
}

export function TextArea({
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={4} className={`${inputCls} ${className}`} {...props} />;
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${inputCls} ${className}`} {...props} />;
}
